<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'
import { verticalSolutionBySlug, verticalSolutions } from '@/data/verticalSolutions'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const { t } = useI18n({ useScope: 'global' })

const solution = computed(() => verticalSolutionBySlug(String(route.params.slug || '')))
const otherSolutions = computed(() =>
  verticalSolutions.filter(s => s.slug !== solution.value?.slug),
)

const setupSEO = () => {
  if (typeof document === 'undefined' || !solution.value) return

  const { seoTitle, seoDescription } = solution.value
  const url = window.location.href
  const image = `${window.location.origin}/images/sito-seo.jpg`

  document.title = seoTitle

  const setMeta = (name: string, content: string, isProperty = false) => {
    let el = document.querySelector(`meta[${isProperty ? 'property' : 'name'}="${name}"]`)
    if (!el) {
      el = document.createElement('meta')
      if (isProperty) el.setAttribute('property', name)
      else el.setAttribute('name', name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }

  setMeta('description', seoDescription)
  setMeta('og:title', seoTitle, true)
  setMeta('og:description', seoDescription, true)
  setMeta('og:url', url, true)
  setMeta('og:type', 'website', true)
  setMeta('og:image', image, true)
  setMeta('twitter:card', 'summary_large_image')
  setMeta('twitter:title', seoTitle)
  setMeta('twitter:description', seoDescription)
}

onMounted(() => {
  setupSEO()
})

watch(solution, setupSEO)
</script>

<template>
  <div v-if="solution" class="solution-premium">
    <PublicSiteHeader />

    <!-- Hero -->
    <header class="solution-hero">
      <div class="container hero-grid">
        <div class="hero-text">
          <div class="badge mb-6">{{ solution.moduleTitle }}</div>
          <h1 class="text-h1 hero-title mb-6">{{ solution.h1 }}</h1>
          <p class="text-h6 hero-description mb-10">{{ solution.lead }}</p>
          <div class="d-flex flex-wrap gap-4">
            <VBtn size="x-large" color="primary" to="/register" class="btn-glow px-10 py-4 text-h6 font-weight-bold">
              {{ t('publicSite.shared.startFree') }}
            </VBtn>
            <VBtn size="x-large" variant="outlined" color="white" :to="`/modules/${solution.moduleSlug}`" class="px-10 py-4 text-h6 font-weight-bold">
              Scopri il modulo {{ solution.moduleTitle }}
            </VBtn>
          </div>
        </div>
        <div v-if="solution.image" class="hero-visual">
          <div class="glow-sphere"></div>
          <img :src="solution.image" :alt="solution.h1" class="main-mockup">
        </div>
      </div>
    </header>

    <!-- Bullets -->
    <section class="features-section py-16">
      <div class="container" style="max-width: 760px;">
        <ul class="bullet-list">
          <li v-for="bullet in solution.bullets" :key="bullet" class="d-flex align-start mb-6">
            <VIcon icon="tabler-circle-check" color="primary" size="24" class="me-4 mt-1 flex-shrink-0" />
            <span class="text-h6 bullet-text">{{ bullet }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- Cross-sell other verticals -->
    <section class="other-solutions py-16">
      <div class="container">
        <h2 class="text-h4 font-weight-bold section-title text-center mb-10">Anche per il tuo settore</h2>
        <div class="d-flex flex-wrap justify-center gap-4">
          <RouterLink
            v-for="s in otherSolutions"
            :key="s.slug"
            :to="`/soluzioni/${s.slug}`"
            class="other-chip"
          >
            {{ s.title }}
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-banner">
      <div class="container text-center py-16">
        <div class="cta-inner glass-effect pa-16 rounded-xl overflow-hidden position-relative">
          <h2 class="text-h2 font-weight-black cta-title mb-8 position-relative">
            {{ t('publicSite.landing.ctaTitle') }}
          </h2>
          <VBtn size="x-large" color="white" class="text-primary px-16 py-6 text-h6 font-weight-bold position-relative" to="/register">
            {{ t('publicSite.shared.createYourAppNow') }}
          </VBtn>
        </div>
      </div>
    </section>

    <PublicSiteFooter />
  </div>

  <div v-else class="solution-premium d-flex align-center justify-center" style="min-height:100vh;">
    <div class="text-center">
      <p class="text-h5 mb-6" style="color:#F1F5F9;">Soluzione non trovata.</p>
      <VBtn color="primary" to="/">Torna alla home</VBtn>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.solution-premium {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
  overflow-x: hidden;
}

.container { max-width: 1600px; margin: 0 auto; padding: 0 40px; }

.solution-hero { padding: 100px 0 60px; }
.hero-grid { display: grid; grid-template-columns: 1.1fr 0.9fr; align-items: center; gap: 60px; }
.badge {
  display: inline-block; padding: 8px 16px; background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2); border-radius: 100px; color: #A5B4FC;
  font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 0.06em;
}
.hero-title { color: #FFFFFF; }
.hero-description { color: #E5EEF9; max-width: 560px; }
.hero-visual { position: relative; display: flex; justify-content: center; }
.main-mockup {
  width: 100%; max-width: 440px; height: auto; border-radius: 24px;
  box-shadow: 0 50px 100px -20px rgba(0, 0, 0, 0.5); position: relative; z-index: 2;
}
.glow-sphere {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: 140%; height: 140%; background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
  filter: blur(80px); z-index: 1;
}

.bullet-list { list-style: none; padding: 0; margin: 0; }
.bullet-text { color: #E5EEF9; }

.section-title { color: #FFFFFF; }
.other-chip {
  font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 13.5px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px; padding: 8px 18px; color: #E5EEF9; text-decoration: none; transition: all 0.25s ease;
}
.other-chip:hover { border-color: #6366F1; color: #FFFFFF; background: rgba(99, 102, 241, 0.1); }

.cta-inner { background: #1E293B; border: 1px solid rgba(255, 255, 255, 0.1); }
.cta-title { color: #FFFFFF; }

@media (max-width: 960px) {
  .hero-grid { grid-template-columns: 1fr; text-align: center; }
  .hero-description { margin-left: auto; margin-right: auto; }
  .d-flex.gap-4 { justify-content: center; }
}

@media (max-width: 600px) {
  .container { padding: 0 20px; }
}
</style>
